import { useEffect } from 'react';

import { KeyCodes } from 'lib/constants/keyCodes';

// allow list for enabling keyboard shortcuts on document elements
// we don't want to perform any shortcuts when the user is focused on an input element
export const PERMITTED_DOCUMENT_TAGS = ['BODY', 'BUTTON', 'H1'];

interface KeyListenerOptions {
  stopPropagation?: boolean;
  preventDefault?: boolean;
}

interface KeyCombinationOptions {
  withAlt?: boolean;
  withCtrl?: boolean;
  withShift?: boolean;
}

const factoryUseKeyListener =
  (keyCode: KeyCodes, options: KeyListenerOptions = {}) =>
  (listener: (evt: KeyboardEvent) => void) => {
    const { stopPropagation, preventDefault } = options;
    return useEffect(() => {
      const handleKeyUp = (evt: KeyboardEvent) => {
        if (stopPropagation) evt.stopPropagation();
        if (preventDefault) evt.preventDefault();
        evt.keyCode === keyCode && listener(evt);
      };
      window.addEventListener('keyup', handleKeyUp);
      return () => window.removeEventListener('keyup', handleKeyUp);
    }, [listener]);
  };
const factoryUseKeyCombinationListener =
  (keyCode: KeyCodes, options: KeyListenerOptions & KeyCombinationOptions = {}) =>
  (listener: (evt: KeyboardEvent) => void) => {
    const { stopPropagation, preventDefault, withAlt, withCtrl, withShift } = options;
    return useEffect(() => {
      const handleKeyUp = (evt: KeyboardEvent) => {
        if (stopPropagation) evt.stopPropagation();
        if (preventDefault) evt.preventDefault();
        if (withAlt && !evt.altKey) {
          return;
        }
        if (withCtrl && !evt.ctrlKey && !evt.metaKey) {
          return;
        }
        if (withShift && !evt.shiftKey) {
          return;
        }
        return evt.keyCode === keyCode && listener(evt);
      };
      window.addEventListener('keydown', handleKeyUp);
      return () => window.removeEventListener('keydown', handleKeyUp);
    }, [listener]);
  };

export default factoryUseKeyListener;

// Single key listeners
export const useEscListener = factoryUseKeyListener(KeyCodes.Esc);
export const useLeftArrowListener = factoryUseKeyListener(KeyCodes.Left);
export const useRightArrowListener = factoryUseKeyListener(KeyCodes.Right);
export const useUpArrowListener = factoryUseKeyListener(KeyCodes.Up);
export const useDownArrowListener = factoryUseKeyListener(KeyCodes.Down);
export const useDeleteListener = factoryUseKeyListener(KeyCodes.Backspace);

// Key combination listeners
export const useKeydownUndoListener = factoryUseKeyCombinationListener(KeyCodes.Z, {
  withCtrl: true,
});
export const useKeydownRedoListener = factoryUseKeyCombinationListener(KeyCodes.Z, {
  withCtrl: true,
  withShift: true,
});
export const useKeydownSaveListener = factoryUseKeyCombinationListener(KeyCodes.S, {
  withCtrl: true,
});
export const useKeydownZoomInListener = (options?: KeyListenerOptions | KeyCombinationOptions) =>
  factoryUseKeyCombinationListener(KeyCodes.EqualSign, {
    withCtrl: true,
    ...options,
  });
export const useKeydownZoomOutListener = (options?: KeyListenerOptions | KeyCombinationOptions) =>
  factoryUseKeyCombinationListener(KeyCodes.Dash, {
    withCtrl: true,
    ...options,
  });
export const useKeydownZoomResetListener = (options?: KeyListenerOptions | KeyCombinationOptions) =>
  factoryUseKeyCombinationListener(KeyCodes.Zero, {
    withCtrl: true,
    ...options,
  });
export const useKeydownDeleteListener = factoryUseKeyCombinationListener(KeyCodes.Backspace);
export const useKeydownCopyListener = factoryUseKeyCombinationListener(KeyCodes.C, {
  withCtrl: true,
});
export const useKeyDownPasteListener = factoryUseKeyCombinationListener(KeyCodes.V, {
  withCtrl: true,
});
